(() => {
  const selector = '[data-two-nu-hexachroma-comparison]';
  const rangeSelector = '[data-two-nu-hexachroma-range]';
  const version = 'scroll-story-v6-discrete-steps';
  const pressedNavigationDelay = 160;
  const pressedFailsafeDelay = 420;

  const clamp = (value, minimum = 0, maximum = 100) => (
    Math.min(maximum, Math.max(minimum, value))
  );

  const setPosition = (root, value, range = null) => {
    const position = Math.round(clamp(Number(value) || 0) * 100) / 100;
    root.style.setProperty('--two-nu-hex-position', `${position}%`);
    root.dataset.twoNuHexachromaPosition = String(position);
    root.dataset.twoNuHexachromaBeforeWidth = String(position);
    if (root.dataset.twoNuHexachromaPlacement === 'home') {
      root.dataset.twoNuHexachromaCtaTone = position <= 50 ? 'dark' : 'light';
    }
    if (range) {
      range.value = String(Math.round(position));
      range.setAttribute('aria-valuetext', `${Math.round(position)}%`);
    }
  };

  const addListener = (cleanups, target, eventName, listener, options) => {
    if (!target?.addEventListener) return;
    target.addEventListener(eventName, listener, options);
    cleanups.push(() => target.removeEventListener(eventName, listener, options));
  };

  const initialiseManual = (root, cleanups) => {
    const range = root.querySelector(rangeSelector);
    if (!range) return false;

    let activePointerId = null;

    const sync = () => setPosition(root, Number(range.value), range);

    const syncFromPointer = (event) => {
      const bounds = range.getBoundingClientRect();
      if (!bounds.width) return;
      const value = ((event.clientX - bounds.left) / bounds.width) * 100;
      setPosition(root, value, range);
    };

    const start = (event) => {
      if (event.isPrimary === false) return;
      activePointerId = event.pointerId;
      root.classList.add('is-dragging');
      range.focus({ preventScroll: true });
      syncFromPointer(event);
      try {
        range.setPointerCapture(event.pointerId);
      } catch {
        // Native range behaviour remains available when capture is unsupported.
      }
    };

    const move = (event) => {
      if (activePointerId !== event.pointerId) return;
      syncFromPointer(event);
    };

    const stop = (event) => {
      if (event?.pointerId != null && activePointerId !== event.pointerId) return;
      activePointerId = null;
      root.classList.remove('is-dragging');
    };

    addListener(cleanups, range, 'input', sync, { passive: true });
    addListener(cleanups, range, 'change', sync, { passive: true });
    addListener(cleanups, range, 'pointerdown', start);
    addListener(cleanups, range, 'pointermove', move, { passive: true });
    addListener(cleanups, range, 'pointerup', stop, { passive: true });
    addListener(cleanups, range, 'pointercancel', stop, { passive: true });
    addListener(cleanups, range, 'lostpointercapture', stop, { passive: true });
    addListener(cleanups, range, 'blur', () => stop());

    root.dataset.twoNuHexachromaControl = 'manual';
    sync();
    return true;
  };

  const initialiseScrollStory = (root, cleanups) => {
    const stage=root.querySelector('.two-nu-hexachroma-comparison__stage');
    if(!stage)return false;
    const reduced=window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame=0,touch=null,settleTimer=0,wheelTimer=0,wheelCaptured=false;
    const host=()=>/auto|scroll/.test(getComputedStyle(document.body).overflowY)&&document.body.scrollHeight>document.body.clientHeight+2?document.body:document.scrollingElement;
    const geometry=()=>{const h=Math.max(1,stage.getBoundingClientRect().height),top=root.getBoundingClientRect().top;return{h,top,start:host().scrollTop+top};};
    const inside=g=>g.top<=48&&g.top>=-g.h-48;
    const schedule=()=>{if(!frame)frame=requestAnimationFrame(update);};
    function update(){
      frame=0;if(!root.isConnected)return;
      const g=geometry();root.dataset.twoNuHexachromaStepActive=String(inside(g));
      let value=inside(g)?(g.top < -g.h/2?15:85):g.top>=g.h?100:g.top>=0?100-15*(1-g.top/g.h):g.top>=-g.h*2?15*(1-(-g.top-g.h)/g.h):0;
      setPosition(root,value);
    }
    function step(direction){
      const g=geometry();if(!inside(g)||settleTimer)return false;
      const revealed=g.top < -g.h/2;
      const offset=direction>0?(revealed?root.getBoundingClientRect().height:g.h):(revealed?0:-g.h);
      const target=Math.max(0,g.start+offset);
      root.dataset.twoNuHexachromaSettling='true';
      host().scrollTo({top:target,behavior:reduced.matches?'auto':'smooth'});
      settleTimer=setTimeout(()=>{settleTimer=0;root.dataset.twoNuHexachromaSettling='false';host().scrollTo({top:target,behavior:'auto'});schedule();},500);
      return true;
    }
    const eligible=target=>!target.closest?.('a,button,input,textarea,select,[role="dialog"]');
    addListener(cleanups,stage,'touchstart',e=>{if(e.touches.length===1&&inside(geometry())&&eligible(e.target))touch={x:e.touches[0].clientX,y:e.touches[0].clientY,direction:0};},{passive:true});
    addListener(cleanups,stage,'touchmove',e=>{if(!touch||e.touches.length!==1)return;const x=e.touches[0].clientX-touch.x,y=e.touches[0].clientY-touch.y;if(Math.abs(y)>12&&Math.abs(y)>Math.abs(x))touch.direction=y<0?1:-1;if(e.cancelable)e.preventDefault();},{passive:false});
    const end=()=>{const direction=touch?.direction;touch=null;if(direction)step(direction);};
    addListener(cleanups,stage,'touchend',end,{passive:true});
    addListener(cleanups,stage,'touchcancel',()=>{touch=null;},{passive:true});
    addListener(cleanups,window,'wheel',e=>{
      if(e.ctrlKey||Math.abs(e.deltaY)<=Math.abs(e.deltaX)||!eligible(e.target))return;
      if(!wheelCaptured&&(!root.contains(e.target)||!inside(geometry())))return;
      if(e.cancelable)e.preventDefault();
      clearTimeout(wheelTimer);
      if(!wheelCaptured){wheelCaptured=true;step(e.deltaY>0?1:-1);}
      wheelTimer=setTimeout(()=>{wheelCaptured=false;},180);
    },{passive:false});
    addListener(cleanups,stage,'keydown',e=>{if(!eligible(e.target))return;const direction=['ArrowDown','PageDown',' '].includes(e.key)?1:['ArrowUp','PageUp'].includes(e.key)?-1:0;if(direction&&step(direction))e.preventDefault();});
    for(const target of [window,document.body,document])addListener(cleanups,target,'scroll',schedule,{passive:true,capture:true});
    for(const name of ['resize','orientationchange','pageshow','twoNu:viewportchange'])addListener(cleanups,window,name,schedule,{passive:true});
    addListener(cleanups,window.visualViewport,'resize',schedule,{passive:true});
    if('ResizeObserver'in window){const observer=new ResizeObserver(schedule);observer.observe(stage);cleanups.push(()=>observer.disconnect());}
    cleanups.push(()=>{clearTimeout(settleTimer);clearTimeout(wheelTimer);if(frame)cancelAnimationFrame(frame);});
    root.dataset.twoNuHexachromaSettling='false';root.dataset.twoNuHexachromaControl='step-scroll';setPosition(root,100);schedule();return true;
  };

  const initialiseHomeCtaFeedback = (root, cleanups) => {
    const cta = root.querySelector('.two-nu-hexachroma-comparison__cta');
    if (!cta) return;

    let feedbackTimer = 0;
    let navigationTimer = 0;

    const clearFeedback = () => {
      if (feedbackTimer) window.clearTimeout(feedbackTimer);
      feedbackTimer = 0;
      cta.classList.remove('is-pressed');
    };

    const clearAll = () => {
      clearFeedback();
      if (navigationTimer) window.clearTimeout(navigationTimer);
      navigationTimer = 0;
    };

    const showFeedback = () => {
      clearFeedback();
      cta.classList.add('is-pressed');
      feedbackTimer = window.setTimeout(clearFeedback, pressedFailsafeDelay);
    };

    const isPrimaryUnmodified = (event) => (
      event.button === 0
      && !event.metaKey
      && !event.ctrlKey
      && !event.shiftKey
      && !event.altKey
    );

    const sameTabSameOriginHref = () => {
      const target = (cta.getAttribute('target') || '').toLowerCase();
      if ((target && target !== '_self') || cta.hasAttribute('download')) return '';
      try {
        const url = new URL(cta.href, window.location.href);
        return url.origin === window.location.origin && /^https?:$/.test(url.protocol)
          ? url.href
          : '';
      } catch {
        return '';
      }
    };

    addListener(cleanups, cta, 'pointerdown', (event) => {
      if (event.isPrimary === false || !isPrimaryUnmodified(event)) return;
      showFeedback();
    }, { passive: true });
    addListener(cleanups, cta, 'pointercancel', clearFeedback, { passive: true });
    addListener(cleanups, cta, 'dragstart', clearFeedback, { passive: true });
    addListener(cleanups, cta, 'blur', clearFeedback, { passive: true });
    addListener(cleanups, cta, 'keydown', (event) => {
      if (event.key === 'Enter' && !event.repeat) showFeedback();
    });
    addListener(cleanups, cta, 'click', (event) => {
      if (!isPrimaryUnmodified(event)) return;
      showFeedback();

      const href = sameTabSameOriginHref();
      if (!href || event.defaultPrevented) return;

      event.preventDefault();
      if (navigationTimer) return;
      navigationTimer = window.setTimeout(() => {
        navigationTimer = 0;
        window.location.assign(href);
      }, pressedNavigationDelay);
    });
    addListener(cleanups, window, 'pagehide', clearAll);
    addListener(cleanups, window, 'pageshow', clearAll);
    cleanups.push(clearAll);
  };

  const initialise = (root) => {
    if (!root || root.dataset.twoNuHexachromaVersion === version) return;
    root.twoNuHexachromaCleanup?.();

    const cleanups = [];
    const placement = root.dataset.twoNuHexachromaPlacement || 'home';
    const ready = placement === 'home'
      ? initialiseScrollStory(root, cleanups)
      : initialiseManual(root, cleanups);
    if (!ready) return;
    if (placement === 'home') initialiseHomeCtaFeedback(root, cleanups);

    root.twoNuHexachromaCleanup = () => {
      cleanups.splice(0).forEach((cleanup) => cleanup());
      root.classList.remove('is-dragging');
      delete root.twoNuHexachromaCleanup;
      delete root.dataset.twoNuHexachromaVersion;
    };
    root.dataset.twoNuHexachromaReady = 'true';
    root.dataset.twoNuHexachromaVersion = version;
  };

  const initialiseAll = (scope = document) => {
    if (scope.matches?.(selector)) initialise(scope);
    scope.querySelectorAll?.(selector).forEach(initialise);
  };

  const boot = () => initialiseAll();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(selector)
      ? event.target
      : event.target.querySelector?.(selector);
    root?.twoNuHexachromaCleanup?.();
  });
})();
